import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ClientErrorBoundary, ClientRouteObserver } from './observability/ClientEventBridge';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AppendiceScreen from './screens/AppendiceScreen';
import AppendiceTheoryScreen from './screens/AppendiceTheoryScreen';
import AppendiceDomandeScreen from './screens/AppendiceDomandeScreen';
import PrincipleDetailScreen from './screens/PrincipleDetailScreen';
import SignIndexScreen from './screens/SignIndexScreen';
import ProfScreen from './screens/ProfScreen';
import SimulaScreen from './screens/SimulaScreen';
import ProfileScreen from './screens/ProfileScreen';
import MessagesScreen from './screens/MessagesScreen';
import MioProgressoScreen from './screens/MioProgressoScreen';
import AccountPausedScreen from './screens/AccountPausedScreen';
import SimulaPostMortemScreen from './screens/SimulaPostMortemScreen';
import StaffApp from './screens/staff/StaffApp';
import ArgomentoDetailScreen from './screens/ArgomentoDetailScreen';
import DeepLessonRedirect from './screens/DeepLessonRedirect';
import GuidedLessonScreen from './screens/GuidedLessonScreen';
import { JoinScreen } from './screens/JoinScreen';
import { LoginScreen } from './screens/LoginScreen';
import { OnboardingTour, hasSeenTour } from './components/OnboardingTour';
import { AIActDisclosureModal } from './components/AIActDisclosureModal';
import { AppFrame } from './components/AppFrame';
import { ApiError, getToken, setToken, subscribeToInvalidStudentSession } from './api/client';
import { studentProfileApi, type StudentMe } from './api/student-profile';
import { loadFeatureFlags } from './state/feature-flags';
import { useUnreadMessages } from './state/unread-messages';
import { getStoredFirstName, getStoredExamDate, replaceStoredProfileFromServer } from './state/profile';
import { reconcileStudentOwner } from './state/logout-hygiene';
import { canonicalLessonRouteForMicroTopic } from './state/lesson-entry';

// Re-export the profile getters so existing importers (`../App`) keep working
// after the storage moved to state/profile.ts (P6 STEP 3).
export { getStoredFirstName, getStoredExamDate };

// ---------------------------------------------------------------------------
// AuthGate — validates the current student token before mounting student state.
// ---------------------------------------------------------------------------

export type AuthState =
  | { status: 'checking' }
  | { status: 'authenticated'; me: StudentMe }
  | { status: 'unauthenticated'; reason: 'missing_token' | 'invalid_session' }
  | { status: 'retryable_error'; reason: 'service' | 'local_privacy' };

interface AuthGateProps {
  children: ReactNode;
  reloadPage?: () => void;
}

const INVALID_SESSION_NOTICE =
  'La sessione non è più valida. Accedi di nuovo per continuare. I dati recuperabili su questo dispositivo saranno ripristinati solo per lo stesso account.';
const LOCAL_PRIVACY_ERROR =
  'Non riesco a preparare questo dispositivo in modo sicuro. Ricarica l’app e riprova.';

function reloadBrowserPage(): void {
  window.location.reload();
}

function initialAuthState(): AuthState {
  return getToken()
    ? { status: 'checking' }
    : { status: 'unauthenticated', reason: 'missing_token' };
}

export function AuthGate({ children, reloadPage = reloadBrowserPage }: AuthGateProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);
  const validationRun = useRef(0);
  const invalidTransitionHandled = useRef(false);
  const reloadTriggered = useRef(false);
  const activeValidation = useRef<{ controller: AbortController; token: string } | null>(null);
  // Tour-gating: show the 3-screen onboarding tour once, right after
  // successful first-login. Returning students (tour flag already set) skip
  // the tour entirely. We track this at the gate level so the tour only
  // appears when the student is authenticated AND hasn't seen it.
  const [tourDone, setTourDone] = useState<boolean>(() => hasSeenTour());

  const abortActiveValidation = useCallback(() => {
    activeValidation.current?.controller.abort();
    activeValidation.current = null;
  }, []);

  const enterInvalidSession = useCallback(() => {
    abortActiveValidation();
    if (invalidTransitionHandled.current) return;
    invalidTransitionHandled.current = true;
    validationRun.current += 1;
    setAuthState({ status: 'unauthenticated', reason: 'invalid_session' });
    navigate('/login', { replace: true });
  }, [abortActiveValidation, navigate]);

  // Register before boot validation starts so the client's captured-token 401
  // transition cannot race past the React gate.
  useEffect(() => subscribeToInvalidStudentSession(enterInvalidSession), [enterInvalidSession]);

  const validateCurrentToken = useCallback(async (newlyStoredToken: boolean) => {
    const capturedToken = getToken();
    if (!capturedToken) {
      abortActiveValidation();
      validationRun.current += 1;
      setAuthState({ status: 'unauthenticated', reason: 'missing_token' });
      return;
    }

    if (newlyStoredToken) invalidTransitionHandled.current = false;
    const currentValidation = activeValidation.current;
    if (
      currentValidation
      && currentValidation.token === capturedToken
      && !currentValidation.controller.signal.aborted
    ) {
      return;
    }

    abortActiveValidation();
    const controller = new AbortController();
    const activeRequest = { controller, token: capturedToken };
    activeValidation.current = activeRequest;
    const run = ++validationRun.current;
    setAuthState({ status: 'checking' });

    try {
      const me = await studentProfileApi.me(controller.signal);
      if (controller.signal.aborted || run !== validationRun.current || getToken() !== capturedToken) return;

      const ownerResult = reconcileStudentOwner(me.studentId);
      if (controller.signal.aborted || run !== validationRun.current || getToken() !== capturedToken) return;
      if (ownerResult.status === 'failure') {
        setAuthState({ status: 'retryable_error', reason: 'local_privacy' });
        return;
      }
      if (ownerResult.status === 'reload_required') {
        if (!reloadTriggered.current) {
          reloadTriggered.current = true;
          reloadPage();
        }
        return;
      }

      const profileResult = replaceStoredProfileFromServer(me);
      if (profileResult.status === 'failure') {
        setAuthState({ status: 'retryable_error', reason: 'local_privacy' });
        return;
      }
      if (controller.signal.aborted || run !== validationRun.current || getToken() !== capturedToken) return;

      setAuthState({ status: 'authenticated', me });
    } catch (error) {
      if (controller.signal.aborted || run !== validationRun.current) return;
      if (error instanceof ApiError && error.status === 401) {
        const currentToken = getToken();
        if (currentToken !== capturedToken) return;
        setToken(null);
        enterInvalidSession();
        return;
      }
      if (getToken() !== capturedToken) return;
      setAuthState({ status: 'retryable_error', reason: 'service' });
    } finally {
      if (activeValidation.current === activeRequest) activeValidation.current = null;
    }
  }, [abortActiveValidation, enterInvalidSession, reloadPage]);

  const bootValidation = useRef(validateCurrentToken);
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void bootValidation.current(true);
    });
    return () => {
      cancelled = true;
      abortActiveValidation();
      validationRun.current += 1;
    };
  }, [abortActiveValidation]);

  useEffect(() => {
    if (
      authState.status === 'authenticated'
      && (location.pathname === '/login' || location.pathname.startsWith('/join/'))
    ) {
      navigate('/', { replace: true });
    }
  }, [authState.status, location.pathname, navigate]);

  // W2.5: load feature flags once, immediately after auth confirms. Fire-and-forget
  // — components read via useFeatureFlag and fail-closed (false) until loaded.
  // FP8: hydrate the unread-message count at app mount (not just on drawer
  // open) so a fresh retention nudge shows the hamburger badge immediately.
  useEffect(() => {
    if (authState.status === 'authenticated') {
      void loadFeatureFlags();
      void useUnreadMessages.getState().refresh();
    }
  }, [authState.status]);

  if (authState.status === 'checking') {
    return <div role="status">Verifico il tuo accesso…</div>;
  }

  if (authState.status === 'retryable_error' && authState.reason === 'service') {
    return (
      <section>
        <h1>Non riesco a verificare l’accesso</h1>
        <p>Il tuo accesso è ancora salvato su questo dispositivo. Controlla la connessione e riprova.</p>
        <button type="button" onClick={() => { void validateCurrentToken(false); }}>Riprova</button>
      </section>
    );
  }

  if (authState.status === 'retryable_error') {
    return <p role="alert">{LOCAL_PRIVACY_ERROR}</p>;
  }

  if (authState.status === 'authenticated' && !tourDone) {
    return <OnboardingTour onDone={() => setTourDone(true)} />;
  }
  if (authState.status === 'authenticated') {
    // W5 Session 3 — AI Act Art 13 disclosure overlays the main shell on the
    // first authenticated render after the onboarding tour. The modal
    // self-gates on `localStorage` `asc_ai_disclosure_v1`; this branch is
    // reached only when the student is authenticated and the tour has already
    // completed. Once `Capito` (or Esc) flips the localStorage flag, the modal
    // returns null on every subsequent render.
    return (
      <>
        {children}
        <AIActDisclosureModal />
      </>
    );
  }
  // Pilot-readiness P3 (founder B1, 2026-07-07): student invite codes are
  // retired. Logged-out users land on /login; a /join/<slug> link (the
  // school's registration link) shows the registration screen. AuthGate is
  // rendered inside BrowserRouter (AppShell), so useLocation is available.
  const joinMatch = /^\/join\/([^/]+)/.exec(location.pathname);
  const onAuthed = () => {
    void validateCurrentToken(true);
  };
  const authScreen = joinMatch
    ? <JoinScreen slug={decodeURIComponent(joinMatch[1]).toLowerCase()} onAuthed={onAuthed} />
    : <LoginScreen onAuthed={onAuthed} />;
  return (
    <>
      {authState.reason === 'invalid_session' && <p role="alert">{INVALID_SESSION_NOTICE}</p>}
      {authScreen}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner app — uses router hooks (must be inside BrowserRouter)
// ---------------------------------------------------------------------------

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ProfScreen />} />
      <Route path="/prof" element={<ProfScreen />} />
      {/* The voice-MESSAGE recorder screen was removed in the V28xFP merge
          (composer-mic-dictation: mic dictates into the field; the record→send
          path is dropped). The persisted route id stays as a stub-redirect to
          /prof so old links / resume flows never 404. */}
      <Route path="/prof/messaggio-vocale" element={<Navigate to="/prof" replace />} />
      {/* Legacy Chiedi alias for existing links and resume flows. */}
      <Route path="/chiedi" element={<ProfScreen />} />
      {/* Legacy Allena route removed from the student product surface. */}
      <Route path="/allena" element={<Navigate to="/progresso" replace />} />
      <Route path="/simula" element={<SimulaScreen />} />
      <Route path="/appendice" element={<AppendiceScreen />} />
      {/* FP6 Appendice v2 — "Segnali" sign index + detail. Static segments are
          ranked above the dynamic :argomentoId by React Router, so "segnali"
          never collides with an argomento id. */}
      <Route path="/appendice/segnali" element={<SignIndexScreen />} />
      <Route path="/appendice/segnali/:signId" element={<SignIndexScreen />} />
      <Route path="/appendice/:argomentoId" element={<AppendiceScreen />} />
      {/* R2-2: per-argomento full THEORY chapter. Static `teoria` + `domande`
          segments rank above the dynamic `:principleId` route below them in
          React Router v7, so they never collide with a principle id. */}
      <Route path="/appendice/:argomentoId/teoria" element={<AppendiceTheoryScreen />} />
      {/* FP6 — per-argomento official-question browser (separate route, not a tab). */}
      <Route path="/appendice/:argomentoId/domande" element={<AppendiceDomandeScreen />} />
      <Route path="/appendice/:argomentoId/:principleId" element={<PrincipleDetailScreen />} />
      <Route path="/profilo" element={<ProfileScreen />} />
      {/* Legacy Studia landing is not a product destination anymore; lesson deep
          links below stay alive for Fast/Deep tutor-routed flows. */}
      <Route path="/studia" element={<Navigate to="/progresso" replace />} />
      <Route path="/studia/argomento/:argomentoId" element={<ArgomentoDetailScreen />} />
      <Route path="/studia/lezione/fast/:microTopicId" element={<LegacyCanonicalLessonRedirect />} />
      {/* V29 S8: the deep alias resolves to the argomento's first incomplete
          GUIDED lesson (route persisted; AdaptiveLessonScreen retired). */}
      <Route path="/studia/lezione/deep/:argomentoId" element={<DeepLessonRedirect />} />
      {/* FP5 — guided-lesson reader (the 338 baked chapters / woven quizzes).
          Server-flag-gated content; the entry point into this route is gated
          client-side by `guided_lesson_serve_enabled` in ArgomentoDetailScreen.
          A direct hit while the flag is OFF degrades to a friendly "not
          available yet" (the BE 404). */}
      <Route path="/studia/lezione/guidata/:argomentoId/:lessonId" element={<GuidedLessonScreen />} />
      <Route path="/studia/lezione/guidata/:argomentoId" element={<GuidedArgomentoOnlyRedirect />} />
      <Route path="/studia/punti-chiave/:microTopicId" element={<LegacyCanonicalLessonRedirect />} />
      <Route path="/studia/rivedi/:microTopicId" element={<LegacyCanonicalLessonRedirect />} />
      <Route path="/studia/ripasso/:microTopicId" element={<LegacyCanonicalLessonRedirect />} />
      {/* W6 v1.1 Session 2 — student-side inbox + pausa screens. */}
      <Route path="/messaggi" element={<MessagesScreen />} />
      <Route path="/progresso" element={<MioProgressoScreen />} />
      <Route path="/pausa"    element={<AccountPausedScreen />} />
      {/* W3 Phase C — read-only post-mortem screens. Reached from the
          drawer Cronologia click handlers. The /prof resume path doesn't
          need a new route — the drawer hydrates the chat store + navigates
          to the existing Il Prof surface. */}
      <Route path="/post-mortem/allena/:sessionId" element={<Navigate to="/progresso" replace />} />
      <Route path="/post-mortem/simula/:sessionId" element={<SimulaPostMortemScreen />} />
      {/* An already-authenticated student can land on /join/<slug> (shared
          school link) or /login — and any unknown path must never render an
          empty <Routes>. Fall through to Il Prof. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function GuidedArgomentoOnlyRedirect() {
  const { argomentoId = '' } = useParams<{ argomentoId: string }>();
  return <Navigate to={argomentoId ? `/studia/argomento/${argomentoId}` : '/progresso'} replace />;
}

export function LegacyCanonicalLessonRedirect() {
  const { microTopicId = '' } = useParams<{ microTopicId: string }>();
  const route = canonicalLessonRouteForMicroTopic(microTopicId);
  const fallbackArgomento = /^arg\d+/i.exec(microTopicId)?.[0];
  return <Navigate to={route ?? (fallbackArgomento ? `/studia/argomento/${fallbackArgomento}` : '/progresso')} replace />;
}

export function LegacyPuntiChiaveRedirect() {
  return <LegacyCanonicalLessonRedirect />;
}

export function LegacyLessonRedirect() {
  return <LegacyCanonicalLessonRedirect />;
}

// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------

// W6 v1 Session 1: split routing so /staff/* bypasses the student AuthGate.
// The staff surface owns its own auth state (StaffApp) under a separate
// localStorage key, so the student onboarding tour / invite-code gate never
// appears for staff sessions.
function AppShell() {
  const location = useLocation();
  if (location.pathname.startsWith('/staff')) {
    return (
      <Routes>
        <Route path="/staff/*" element={<StaffApp />} />
      </Routes>
    );
  }
  return (
    <AuthGate>
      <AppFrame>
        <ClientRouteObserver />
        <ClientErrorBoundary>
          <AppRoutes />
        </ClientErrorBoundary>
      </AppFrame>
    </AuthGate>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
